<template>
  <div>
    <b-row>
      <b-col />
      <b-col class="text-right">
        <b-button
          variant="outline-primary"
          class="m-1"
          @click="showCreateModal = true"
        >
          Add event type
        </b-button>
      </b-col>
    </b-row>
    <b-alert
      v-if="hasSystemEvents"
      variant="secondary"
      show
      class="mb-3"
    >
      <div class="alert-body">
        <feather-icon
          icon="LockIcon"
          class="mr-50"
        />
        <span>
          <strong>System Events:</strong> The events marked with
          <b-badge
            variant="light-secondary"
            class="mx-1"
          >
            <feather-icon
              icon="LockIcon"
              size="12"
            />
            System Event
          </b-badge>
          are built-in fallback events that are always available. You cannot edit or delete these events.
        </span>
      </div>
    </b-alert>

    <div v-if="eventTypes.length > 0">
      <div class="position-relative">
        <b-spinner
          v-if="reorderingId !== null"
          variant="primary"
          class="position-absolute"
          style="top: 10px; right: 10px; z-index: 10;"
          label="Reordering..."
        />
        <b-table
          :items="sortedEventTypes"
          :fields="tableFields"
          striped
          hover
          responsive
          show-empty
          empty-text="No event types configured"
          class="mb-0"
          :class="{ 'table-dimmed': reorderingId !== null }"
        >
          <template #cell(order)="{ item, index }">
            <div class="d-flex align-items-center justify-content-between">
              <div class="d-flex align-items-center">
                <b-badge
                  variant="light-secondary"
                  class="mr-2 px-2"
                >
                  {{ item.display_order }}
                </b-badge>
                <span class="font-weight-bold text-muted">#{{ index + 1 }}</span>
              </div>
              <b-button-group
                v-if="!item.is_hardcoded && !item.is_system"
                size="sm"
              >
                <b-button
                  v-b-tooltip.hover
                  variant="outline-secondary"
                  size="sm"
                  title="Move earlier in workflow"
                  :disabled="index === 0 || reorderingId !== null"
                  @click="moveEventUp(item, index)"
                >
                  <feather-icon
                    icon="ChevronUpIcon"
                    size="12"
                  />
                </b-button>
                <b-button
                  v-b-tooltip.hover
                  variant="outline-secondary"
                  size="sm"
                  title="Move later in workflow"
                  :disabled="index === sortedEventTypes.length - 1 || reorderingId !== null"
                  @click="moveEventDown(item, index)"
                >
                  <feather-icon
                    icon="ChevronDownIcon"
                    size="12"
                  />
                </b-button>
              </b-button-group>
              <span
                v-else
                class="text-muted ml-2"
              >
                <feather-icon
                  icon="LockIcon"
                  size="14"
                />
              </span>
            </div>
          </template>

          <template #cell(description)="{ item }">
            <div>
              <div class="font-weight-medium">
                {{ item.description }}
              </div>
              <small class="text-muted">{{ item.slug }}</small>
            </div>
          </template>

          <template #cell(prerequisites)="{ item }">
            <div v-if="item.required_event_types.length > 0">
              <b-badge
                v-for="required in item.required_event_types"
                :key="required.id"
                variant="light"
                class="mr-1 mb-1"
              >
                {{ required.slug }}
              </b-badge>
            </div>
            <span
              v-else
              class="text-muted"
            >None</span>
          </template>

          <template #cell(status)="{ item }">
            <b-badge :variant="item.is_active ? 'success' : 'secondary'">
              {{ item.is_active ? 'Active' : 'Inactive' }}
            </b-badge>
          </template>

          <template #cell(actions)="{ item }">
            <b-button-group
              v-if="!item.is_system && !item.is_hardcoded"
              size="sm"
            >
              <b-button
                variant="outline-primary"
                size="sm"
                @click="editEventType(item)"
              >
                <feather-icon
                  icon="EditIcon"
                  size="12"
                />
              </b-button>
              <b-button
                variant="outline-danger"
                size="sm"
                @click="deleteEventType(item)"
              >
                <feather-icon
                  icon="TrashIcon"
                  size="12"
                />
              </b-button>
            </b-button-group>
            <b-badge
              v-else
              variant="light-secondary"
            >
              System Event
            </b-badge>
          </template>
        </b-table>
      </div>
    </div>

    <div v-else>
      <b-card class="text-center">
        <b-card-body class="py-4">
          <feather-icon
            icon="GitBranchIcon"
            size="32"
            class="text-muted mb-2"
          />
          <p class="text-muted mb-0">
            No event types configured. Click "Add event type" to get started.
          </p>
        </b-card-body>
      </b-card>
    </div>

    <b-modal
      v-model="showCreateModal"
      :title="editingEventType ? 'Edit Event Type' : 'Create Event Type'"
      size="lg"
      @ok="saveEventType"
      @hidden="resetForm"
    >

      <b-form @submit.prevent="saveEventType">
        <b-row>
          <b-col md="8">
            <b-form-group
              label="Event Name"
              label-for="description"
            >
              <b-form-input
                id="description"
                v-model="form.description"
                required
                placeholder="e.g., Package Sorted"
              />
              <small class="text-muted">
                Display name for the event
              </small>
            </b-form-group>
          </b-col>
          <b-col md="4">
            <b-form-group
              label="Workflow Step"
              label-for="display_order"
            >
              <b-form-input
                id="display_order"
                v-model="form.display_order"
                type="number"
                min="1"
                required
              />
              <small class="text-muted">
                Lower numbers occur first
              </small>
            </b-form-group>
          </b-col>
        </b-row>

        <b-form-group
          label="Event Code"
          label-for="slug"
        >
          <b-form-input
            id="slug"
            v-model="form.slug"
            required
            placeholder="e.g., package_sorted"
            :state="slugValidation"
          />
          <small class="text-muted">
            System identifier (lowercase, underscores only)
          </small>
        </b-form-group>

        <b-form-group
          v-if="availableEventTypes.length > 0"
          label="Prerequisites"
        >
          <b-form-checkbox-group
            v-model="form.required_event_type_ids"
            :options="availableEventTypes"
            text-field="description"
            value-field="id"
            stacked
          />
          <small class="text-muted">
            Events that must happen before this event
          </small>
        </b-form-group>

        <b-form-group>
          <b-form-checkbox v-model="form.is_active">
            Active (visible in shipment interfaces)
          </b-form-checkbox>
        </b-form-group>
      </b-form>
    </b-modal>
  </div>
</template>

<script>
import {
  BAlert,
  BBadge,
  BButton,
  BButtonGroup,
  BCard,
  BCardBody,
  BCol,
  BForm,
  BFormCheckbox,
  BFormCheckboxGroup,
  BFormGroup,
  BFormInput,
  BModal,
  BRow,
  BSpinner,
  BTable,
  VBTooltip,
} from 'bootstrap-vue'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

export default {
  name: 'EventWorkflowManager',
  directives: {
    'b-tooltip': VBTooltip,
  },
  components: {
    BCard,
    BCardBody,
    BRow,
    BCol,
    BButton,
    BModal,
    BForm,
    BFormGroup,
    BFormInput,
    BFormCheckbox,
    BFormCheckboxGroup,
    BBadge,
    BButtonGroup,
    BTable,
    BAlert,
    BSpinner,
  },
  props: {
    companyId: {
      type: Number,
      required: true,
    },
  },
  data() {
    return {
      eventTypes: [],
      showCreateModal: false,
      editingEventType: null,
      reorderingId: null,
      form: {
        slug: '',
        description: '',
        display_order: 1,
        is_active: true,
        required_event_type_ids: [],
      },
      tableFields: [
        { key: 'order', label: 'Step', sortable: false },
        { key: 'description', label: 'Event Type', sortable: false },
        { key: 'prerequisites', label: 'Required Events', sortable: false },
        { key: 'status', label: 'Status', sortable: false },
        { key: 'actions', label: 'Actions', sortable: false },
      ],
    }
  },
  computed: {
    sortedEventTypes() {
      return [...this.eventTypes].sort((a, b) => a.display_order - b.display_order)
    },
    hasSystemEvents() {
      return this.eventTypes.some(et => et.is_system === true)
    },
    availableEventTypes() {
      return this.eventTypes.filter(et => !(this.editingEventType && et.id === this.editingEventType.id))
    },
    slugValidation() {
      if (!this.form.slug) return null
      return /^[a-z0-9_]+$/.test(this.form.slug)
    },
  },
  mounted() {
    this.fetchEventTypes()
  },
  methods: {
    async fetchEventTypes() {
      try {
        const response = await this.$http.get(`/v1/api/companies/${this.companyId}/event-types`)
        this.eventTypes = response.data.data || []
      } catch (error) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error',
            text: 'Failed to fetch event types',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      }
    },

    editEventType(eventType) {
      this.editingEventType = eventType
      this.form = {
        slug: eventType.slug,
        description: eventType.description,
        display_order: eventType.display_order,
        is_active: eventType.is_active,
        required_event_type_ids: (eventType.required_event_types || []).map(et => et.id),
      }
      this.showCreateModal = true
    },

    async saveEventType() {
      try {
        const { companyId } = this
        const url = this.editingEventType
          ? `/v1/api/companies/${companyId}/event-types/${this.editingEventType.id}`
          : `/v1/api/companies/${companyId}/event-types`

        const method = this.editingEventType ? 'put' : 'post'

        const payload = {
          slug: this.form.slug,
          description: this.form.description,
          display_order: this.form.display_order,
          is_active: this.form.is_active,
          required_event_type_ids: this.form.required_event_type_ids || [],
        }

        await this.$http[method](url, payload)

        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Success',
            text: `Event type ${this.editingEventType ? 'updated' : 'created'} successfully`,
            icon: 'CheckIcon',
            variant: 'success',
          },
        })

        await this.fetchEventTypes()
        this.showCreateModal = false
        this.$emit('events-updated')
      } catch (error) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error',
            text: error.response?.data?.message || 'Failed to save event type',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      }
    },

    async deleteEventType(eventType) {
      this.$bvModal.msgBoxConfirm(
        `Are you sure you want to delete the event type "${eventType.description}"?`,
        {
          title: 'Confirm Deletion',
          okVariant: 'danger',
          okTitle: 'Delete',
          cancelTitle: 'Cancel',
        },
      ).then(async confirmed => {
        if (confirmed) {
          try {
            await this.$http.delete(`/v1/api/companies/${this.companyId}/event-types/${eventType.id}`)

            this.$toast({
              component: ToastificationContent,
              props: {
                title: 'Success',
                text: 'Event type deleted successfully',
                icon: 'CheckIcon',
                variant: 'success',
              },
            })

            await this.fetchEventTypes()
            this.$emit('events-updated')
          } catch (error) {
            this.$toast({
              component: ToastificationContent,
              props: {
                title: 'Error',
                text: error.response?.data?.message || 'Failed to delete event type',
                icon: 'AlertTriangleIcon',
                variant: 'danger',
              },
            })
          }
        }
      })
    },

    async moveEventUp(eventType, currentIndex) {
      if (currentIndex === 0 || this.reorderingId) return
      await this.reorderEvents(eventType.id, currentIndex, currentIndex - 1)
    },

    async moveEventDown(eventType, currentIndex) {
      if (currentIndex === this.sortedEventTypes.length - 1 || this.reorderingId) return
      await this.reorderEvents(eventType.id, currentIndex, currentIndex + 1)
    },

    async reorderEvents(eventId, fromIndex, toIndex) {
      if (this.reorderingId) return
      this.reorderingId = eventId

      const originalEventTypes = [...this.eventTypes]

      const reorderedEvents = [...this.sortedEventTypes]
      const [movedEvent] = reorderedEvents.splice(fromIndex, 1)
      reorderedEvents.splice(toIndex, 0, movedEvent)

      const violation = this.checkPrerequisiteViolations(reorderedEvents)
      if (violation) {
        this.reorderingId = null
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Invalid Order',
            text: violation,
            icon: 'AlertTriangleIcon',
            variant: 'warning',
          },
        })
        return
      }

      this.eventTypes = reorderedEvents.map((event, index) => ({
        ...event,
        display_order: (index + 1) * 10,
      }))

      this.reorderingId = null

      try {
        const { companyId } = this

        const eventTypesPayload = reorderedEvents
          .map((event, index) => ({
            id: event.id,
            display_order: (index + 1) * 10,
            is_system: event.is_system,
            is_hardcoded: event.is_hardcoded,
          }))
          .filter(event => !event.is_hardcoded && event.id)
          .map(({ id, display_order }) => ({ id, display_order }))

        await this.$http.post(`/v1/api/companies/${companyId}/event-types/reorder`, {
          event_types: eventTypesPayload,
        })

        this.$emit('events-updated')
      } catch (error) {
        this.eventTypes = originalEventTypes

        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Reorder Failed',
            text: error.response?.data?.message || 'Failed to update event order. Changes reverted.',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      }
    },

    checkPrerequisiteViolations(orderedEvents) {
      const positionMap = {}
      orderedEvents.forEach((event, index) => {
        positionMap[event.id] = index
      })

      let violaton = null
      orderedEvents.some(event => {
        if (event.required_event_types && event.required_event_types.length > 0) {
          const eventPos = positionMap[event.id]

          return event.required_event_types.some(prerequisite => {
            const prereqPos = positionMap[prerequisite.id]

            if (prereqPos !== undefined && prereqPos >= eventPos) {
              violaton = `"${event.description}" requires "${prerequisite.description}" to happen first`
              return true
            }
            return false
          })
        }
        return false
      })

      return violaton
    },

    resetForm() {
      this.editingEventType = null
      this.form = {
        slug: '',
        description: '',
        display_order: this.eventTypes.length > 0 ? Math.max(...this.eventTypes.map(et => et.display_order)) + 10 : 10,
        is_active: true,
        required_event_type_ids: [],
      }
    },
  },
}
</script>

<style scoped>
.font-weight-medium {
  font-weight: 500;
}

.table-dimmed {
  opacity: 0.6;
  pointer-events: none;
}
</style>
